import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider, LANGUAGES } from './context/LanguageContext';
import { Navbar } from './components/sections/Navbar';
import { Hero } from './components/sections/Hero';
import { ValueProposition } from './components/sections/ValueProposition';
import { WhoBenefits } from './components/sections/WhoBenefits';
import { Comparison } from './components/sections/Comparison';
import { WhyOffer } from './components/sections/WhyOffer';
import { WhatYouGet } from './components/sections/WhatYouGet';
import { Subscription } from './components/sections/Subscription';
import { Process } from './components/sections/Process';
import { Pricing } from './components/sections/Pricing';
import { Company } from './components/sections/Company';
import { ServiceInfo } from './components/sections/ServiceInfo';
import { Portfolio } from './components/sections/Portfolio';
import { FAQ } from './components/sections/FAQ';
import { Contact } from './components/sections/Contact';
import { Footer } from './components/sections/Footer';
import { ContactForm } from './components/forms/ContactForm';
import { TermsOfService } from './pages/TermsOfService';
import { SubscriptionAgreement } from './pages/SubscriptionAgreement';
import { RefundPolicy } from './pages/RefundPolicy';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { CookiePolicy } from './pages/CookiePolicy';
import { CheckoutPage } from './pages/CheckoutPage';
import './App.css';

const API_URL = 'https://backend-sites.dathent.com';

function MainPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contactOpen, setContactOpen] = useState(false);

  const handleOrderClick = (packageId = 'website_with_subscription') => {
    // Map package IDs to checkout plan names
    const planMap = {
      'website_with_subscription': 'auction',
      'website_full_price': 'one_time',
      'website_full_with_subscription': 'one_time_support'
    };
    const plan = planMap[packageId] || 'auction';
    
    // Extract current language from path
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    
    navigate(`/${lang}/checkout?plan=${plan}`);
  };

  return (
    <>
      <div className="noise-overlay" />
      <Navbar onBriefClick={handleOrderClick} />
      <main>
        <Hero onGetStarted={handleOrderClick} />
        <WhoBenefits />
        <ValueProposition />
        <Comparison />
        <WhyOffer />
        <WhatYouGet />
        <Subscription />
        <Process />
        <Pricing onBriefClick={handleOrderClick} />
        <Company />
        <ServiceInfo />
        <Portfolio onBriefClick={handleOrderClick} />
        <FAQ onContactClick={() => setContactOpen(true)} />
        <Contact 
          onBriefClick={handleOrderClick} 
          onContactClick={() => setContactOpen(true)} 
        />
      </main>
      <Footer />
      
      <ContactForm isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
}

function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  const checkStatus = useCallback(async () => {
    if (!sessionId || attempts >= 5) {
      if (attempts >= 5) setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Clear checkout data on successful payment
          localStorage.removeItem('checkoutFormData');
          localStorage.removeItem('savedBriefId');
          localStorage.removeItem('checkoutReturnTo');
          localStorage.removeItem('checkoutReturnStep');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          setAttempts(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Status check error:', error);
      setAttempts(prev => prev + 1);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === 'checking' && attempts < 5) {
      const timer = setTimeout(checkStatus, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, checkStatus]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">Thank you for your order. We'll be in touch shortly to start your project.</p>
            <button 
              onClick={() => navigate('/')}
              className="text-primary hover:underline"
              data-testid="back-home-btn"
            >
              Back to Home
            </button>
          </>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Issue</h1>
            <p className="text-muted-foreground mb-6">
              {status === 'expired' ? 'Your payment session has expired.' : 'Unable to confirm payment status.'}
              {' '}Please try again or contact us for assistance.
            </p>
            <button 
              onClick={() => navigate('/')}
              className="text-primary hover:underline"
              data-testid="try-again-btn"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function LanguageRedirect() {
  const location = useLocation();
  
  // Get preferred language
  const getPreferredLanguage = () => {
    const saved = localStorage.getItem('dathent-language');
    if (saved && LANGUAGES.find(l => l.code === saved)) return saved;
    
    const browserLang = navigator.language.split('-')[0];
    if (LANGUAGES.find(l => l.code === browserLang)) return browserLang;
    
    return 'en';
  };

  const lang = getPreferredLanguage();
  const newPath = `/${lang}${location.pathname}${location.search}${location.hash}`;
  
  return <Navigate to={newPath} replace />;
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <Routes>
          {/* Routes with language prefix */}
          <Route path="/:lang/" element={<MainPage />} />
          <Route path="/:lang/success" element={<SuccessPage />} />
          <Route path="/:lang/checkout" element={<CheckoutPage />} />
          
          {/* Legal pages */}
          <Route path="/:lang/terms" element={<TermsOfService />} />
          <Route path="/:lang/subscription" element={<SubscriptionAgreement />} />
          <Route path="/:lang/refund" element={<RefundPolicy />} />
          <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
          <Route path="/:lang/cookies" element={<CookiePolicy />} />
          
          {/* Default routes - redirect to language version */}
          <Route path="/" element={<LanguageRedirect />} />
          <Route path="/success" element={<LanguageRedirect />} />
          <Route path="/checkout" element={<LanguageRedirect />} />
          <Route path="/terms" element={<LanguageRedirect />} />
          <Route path="/subscription" element={<LanguageRedirect />} />
          <Route path="/refund" element={<LanguageRedirect />} />
          <Route path="/privacy" element={<LanguageRedirect />} />
          <Route path="/cookies" element={<LanguageRedirect />} />
        </Routes>
      </LanguageProvider>
    </Router>
  );
}

export default App;
